import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  MenuItem,
  MenuList,
  Theme,
  Tooltip,
  useTheme,
} from "@mui/material";
import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  EventEngineAppliesTo,
  hasPermission,
  IAircraft,
  IEventEngineActionTriggerPayload,
  IEventEngineEvent,
  IFlight,
  IFlightError,
  IFlightFlag,
  IFlightFlagSubDoc,
  IGroundEvent,
  PolicyAction,
  PolicyResource,
  unassignedAircraftId,
} from "@vmosolutions/oc-interfaces";
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { checkForConflict, findEventAtPosition } from "src/pages/FlightTimeline/FlightTimeline";
import { TimelineGL } from "src/pages/FlightTimeline/gl/TimelineGL";
import { textualFlightInfoFormat } from "src/pages/FlightTimeline/TimelineUtils";
import { closeModal, openModal, openSnackbar } from "src/redux/actions/App.Actions";
import { getFlightConnections } from "src/redux/actions/flights/FlightData.Actions";
import {
  setBulkAssignmentMode,
  setIsFlightContextOpen,
  setSelectedEvents,
} from "src/redux/actions/flights/FlightTimeline.Actions";
import { openHistoryWindow, openMovementMessageWindow, openWindow } from "src/redux/actions/Mosaic.Actions";
import { MosaicDirection } from "src/utils/MosaicTree";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IFlightErrorMap } from "src/redux/reducers/FlightScannerReducer";
import { IAircraftFlights, isFlight } from "src/redux/reducers/FlightTimelineReducer";
import ContextMenu from "../ContextMenu/ContextMenu";
import { EventModalTypes } from "../DialogModal/DialogModalWrapper";
import FlightSummaryList from "../FlightSummaryList/SummaryList";
import { DayContextMenu } from "./DayContextMenu";
import { DocumentAcknowledgements } from "./DocumentAcknowledgements";
import { EventChipCollection } from "./EventChip";
import { FlightChipCollection } from "./FlightChip";
import { getAircraftIdsFromEvents, swapAircraftBySelectedEvent } from "./helper";
import { SwapACBatchTracker } from "./SwapACBatchTracker";

interface IProps {
  id: string;
  path: string[];
  setActiveEventModal: (activeEventModal: EventModalTypes, data?: any) => void;
  setModalProps: (modalProps: any) => void;
}

const chipContainerStyles = (theme: Theme): object => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  maxWidth: 380,
  userSelect: "none",
  padding: "0px 16px",
});

export const FlightContextMenu = (props: IProps): JSX.Element => {
  const { id, path, setActiveEventModal, setModalProps } = props;
  const theme = useTheme();
  const dispatch = useDispatch();

  const flightData = useSelector((state: IRootState) => state.timelineFlights.flightData);
  const menu = useSelector((state: IRootState) => state.timelineFlights.isFlightContextOpen);
  const centerMenu = useSelector((state: IRootState) => state.authentication.appSettings.flightDisplayMenuCentered);
  const flightFlags = useSelector((state: IRootState) => state.structures.customerStructure.flightFlags);
  const selectedEvents: (IFlight | IGroundEvent)[] = useSelector(
    (state: IRootState) => state.timelineFlights.selectedEvents
  );
  const timelineGLs = useSelector((state: IRootState) => state.timelineFlights.timelineGLs);
  const timelineGL = useMemo(() => {
    const currentTimelineInstance = timelineGLs?.find((timeline: any) => timeline.windowId === id);
    return currentTimelineInstance?.timelineGL ?? null;
  }, [id, timelineGLs]);

  const selectedFlights: IFlight[] = React.useMemo(() => {
    return selectedEvents.filter((event: IFlight | IGroundEvent) => isFlight(event));
  }, [selectedEvents]);

  const selectedUnassigned = useMemo(() => {
    return selectedFlights.filter((flight: IFlight) => !flight.aircraft);
  }, [selectedFlights]);

  const onlyUnassignedSelected = selectedFlights?.length > 0 && selectedFlights.length === selectedUnassigned.length;

  const selectedGroundEvents: IGroundEvent[] = React.useMemo(() => {
    return selectedEvents.length > 0
      ? (selectedEvents.filter((event: IFlight | IGroundEvent) => !isFlight(event)) as IGroundEvent[])
      : [];
  }, [selectedEvents]);

  // Selection belongs to the flight display window this context menu serves.
  const selectedDay = useSelector((state: IRootState) => state.timelineFlights.selectedDay?.[id]);
  const selectedAircraftId = useSelector((state: IRootState) => state.timelineFlights.selectedAircraft?.[id]?.[0]);
  const allAircrafts = useSelector((state: IRootState) => state.structures.customerStructure.aircraft);
  const selectedAircraft = allAircrafts.find((AC: IAircraft) => AC._id === selectedAircraftId);
  const flights: IAircraftFlights = useSelector((state: IRootState) => state.timelineFlights.flightData);
  const flightTimelineCopyTextFormat = useSelector(
    (state: IRootState) => state.UISettings.flightTimelineCopyTextFormat
  );

  const isAPISCustomer = useSelector(
    (state: IRootState) => state.structures.customerStructure.globalAppConfig?.customer?.settings?.isAPISEnabled
  );

  const policies = useSelector((state: IRootState) => state.authentication?.attributes?.policies);
  const flightEditorIsAllowed = useMemo(
    () => hasPermission(policies, PolicyResource.Flight, PolicyAction.Read),
    [policies]
  );
  const groundEventEditorIsAllowed = useMemo(
    () => hasPermission(policies, PolicyResource.GroundEvent, PolicyAction.Read),
    [policies]
  );

  const selectedFlightCount = selectedFlights?.length;
  const flightErrorsMap: IFlightErrorMap = useSelector((state: IRootState) => state.flightScanner.flightErrorsMap);
  const customActions = useSelector((state: IRootState) => state.structures.customerStructure.customActions);
  const customFlightActions = useMemo(() => {
    return customActions?.length > 0
      ? customActions
          .filter(
            (action: IEventEngineEvent) =>
              action.appliesTo === EventEngineAppliesTo.Flights &&
              action?.actionSettings?.maxActionItems >= selectedFlightCount
          )
          .sort((a: IEventEngineEvent, b: IEventEngineEvent) => a?.actionSettings?.order - b?.actionSettings?.order)
      : [];
  }, [customActions, selectedFlightCount]);

  const selectedGroundEventCount = selectedGroundEvents?.length;
  const customGroundEventActions = useMemo(() => {
    return customActions?.length > 0
      ? customActions
          .filter(
            (action: IEventEngineEvent) =>
              action.appliesTo === EventEngineAppliesTo.GroundEvents &&
              action.actionSettings.maxActionItems >= selectedGroundEventCount
          )
          .sort((a: IEventEngineEvent, b: IEventEngineEvent) => a.actionSettings.order - b.actionSettings.order)
      : [];
  }, [customActions, selectedGroundEventCount]);

  const eventEngineIsAllowed = useMemo(
    () => hasPermission(policies, PolicyResource.EventEngine, PolicyAction.Use),
    [policies]
  );

  const handleCustomAction = useCallback(
    async (customAction: IEventEngineEvent, items: IFlight[] | IGroundEvent[]) => {
      const { appliesTo, _id, name } = customAction;

      if (customAction?.actionSettings?.formSchema && !!setActiveEventModal) {
        setActiveEventModal(EventModalTypes.CustomActions);
        setModalProps({ customAction, items });
      } else if (appliesTo && _id && name && items) {
        const notifications: INotifications = {
          dispatch: dispatch,
          infoMessage: `Sending Request to trigger ${name} action...`,
          successMessage: "Request Received Successfully",
          errorMessage: "Error: Unable to send request, please see administrator for assistance.",
        };
        if (items?.length === 1) {
          await AmplifyAPIWrapper.Post<IAircraft>(
            `/event/trigger/${appliesTo}/${_id}/${items[0]._id}`,
            null,
            notifications
          );
        } else {
          const payload: IEventEngineActionTriggerPayload = {
            resourceIds: items.map((item: IFlight | IGroundEvent) => item._id),
            customFields: undefined,
          };
          await AmplifyAPIWrapper.Post<IAircraft>(`/event/trigger/${appliesTo}/${_id}`, payload, notifications);
        }
      }
    },
    [dispatch, setActiveEventModal, setModalProps]
  );

  const [batchId1, setBatchId1] = useState<string>(null);
  const [batchId2, setBatchId2] = useState<string>(null);

  const selectedEventsAircraft: string[] = useMemo(() => {
    return getAircraftIdsFromEvents(selectedFlights);
  }, [selectedFlights]);

  const selectedEventsOnTwoAircraft: boolean = useMemo(() => {
    return selectedEventsAircraft.length === 2 ? true : false;
  }, [selectedEventsAircraft]);

  const selectedGroundEventsAircraft: string[] = useMemo(() => {
    return getAircraftIdsFromEvents(selectedGroundEvents);
  }, [selectedGroundEvents]);

  const selectedGroundEventsOnTwoAircraft: boolean = useMemo(() => {
    return selectedGroundEventsAircraft.length === 2 ? true : false;
  }, [selectedGroundEventsAircraft]);

  const isFlightContextOpen = useMemo(() => {
    const contextMenuState = menu[id];
    return contextMenuState ?? false;
  }, [menu, id]);

  useEffect(() => {
    if (selectedDay) {
      dispatch(setSelectedEvents([]));
    }
  }, [selectedDay, dispatch]);

  const handleCloseContext = useCallback((): void => {
    dispatch(setBulkAssignmentMode(null, [], id));
    dispatch(setIsFlightContextOpen(false, id));
  }, [dispatch, id]);

  useEffect(() => {
    if (selectedFlights.length === 0 && selectedGroundEvents.length === 0 && !selectedDay) {
      dispatch(setIsFlightContextOpen(false, id));
    }
  }, [dispatch, selectedFlights, selectedGroundEvents, selectedDay, id]);

  const handleBetaEditEvent = useCallback(
    (isFlight?: boolean): void => {
      handleCloseContext();
      setTimeout(() => {
        if (isFlight) {
          selectedFlights.forEach((selectedFlight: IFlight) => {
            const windowProps = {
              eventId: selectedFlight?._id,
              isFlight: true,
              aircraftId: selectedFlight?.aircraft?._id,
            };
            const viewId = "BetaEventEditor";
            dispatch(
              openWindow(
                "Flight Editor",
                "BetaEventEditor",
                [],
                windowProps,
                "context",
                true,
                MosaicDirection.Right,
                630
              )
            );
          });
        } else {
          selectedGroundEvents.forEach((selectedGE: IGroundEvent) => {
            const windowProps = {
              eventId: selectedGE?._id,
              isFlight: false,
              aircraftId: selectedGE?.aircraft?._id,
            };
            const viewId = "BetaEventEditor";
            dispatch(
              openWindow("Ground Event Editor", viewId, path, windowProps, "context", true, MosaicDirection.Right, 670)
            );
          });
        }
      }, 100);
    },
    [dispatch, handleCloseContext, path, selectedFlights, selectedGroundEvents]
  );

  const handleSendMovementMessage = (): void => {
    if (!selectedFlights || selectedFlights.length === 0 || selectedFlights.length > 1) {
      return;
    }
    const { path } = props;
    handleCloseContext();
    setTimeout(() => {
      dispatch(openMovementMessageWindow(selectedFlights, path));
    }, 100);
  };

  const selectByFlightNumber = (): void => {
    if (selectedFlights.length !== 1) {
      return;
    }

    const selectedFlight = selectedFlights[0];
    const newSelectedFlights = selectByPredicate(selectedFlight, flightData, selectByFlightNumberPredicate);

    dispatch(setSelectedEvents(newSelectedFlights));
  };

  const selectFlightsUntilReturn = (): void => {
    if (selectedFlights.length !== 1) {
      return;
    }

    const selectedFlight = selectedFlights[0];
    const flightSelectPredicate = (flights: IFlight[], selectedIndex: number): IFlight[] => {
      const origin = flights[selectedIndex].origin;
      const selectedFlights = new Array<IFlight>();
      for (let i = selectedIndex; i < flights.length; i++) {
        const curFlight = flights[i];
        selectedFlights.push(flights[i]);
        if (curFlight.destination === origin) break;
        if (i === flights.length - 1) {
          return [flights[selectedIndex]];
        }
      }
      return selectedFlights;
    };
    const newSelectedFlights = selectByPredicate(selectedFlight, flightData, flightSelectPredicate);
    dispatch(setSelectedEvents(newSelectedFlights));
  };

  const selectByFlightAndReturnFlightNumber = (): void => {
    if (selectedFlights.length !== 1) {
      return;
    }

    const selectedFlight = selectedFlights[0];
    const flightSelectPredicate = (flights: IFlight[], selectedIndex: number): IFlight[] => {
      const number = flights[selectedIndex].flightNumber;
      const origin = flights[selectedIndex].origin;
      const selectedFlights = new Array<IFlight>();
      for (let i = selectedIndex; i < flights.length; i++) {
        const curFlight = flights[i];
        if (curFlight.flightNumber === number || curFlight.flightNumber === number + 1) {
          selectedFlights.push(flights[i]);
          if (curFlight.destination === origin) break;
          if (i === flights.length - 1) {
            return [flights[selectedIndex]];
          }
        } else return [flights[selectedIndex]];
      }
      return selectedFlights;
    };
    const newSelectedFlights = selectByPredicate(selectedFlight, flightData, flightSelectPredicate);
    dispatch(setSelectedEvents(newSelectedFlights));
  };

  const displayWarningModal = async (): Promise<boolean> => {
    return new Promise((resolve) => {
      dispatch(
        openModal({
          root: {
            data: {
              title: "Swap Flight Overlap",
              message:
                "Swapping the selected flights will result in an overlap with an existing ground event. Do you want to continue?",
              buttons: [
                {
                  label: "Cancel",
                  handler: () => {
                    dispatch(closeModal({ root: { data: null } }));
                    dispatch(setSelectedEvents([]));
                    resolve(false);
                  },
                },
                {
                  label: "Continue",
                  handler: () => {
                    dispatch(closeModal({ root: { data: null } }));
                    resolve(true);
                  },
                },
              ],
            },
          },
        })
      );
    });
  };

  const checkConflictBeforeSwap = (
    selectedFlights: IFlight[] | IGroundEvent[],
    aircraftIds: string[],
    allAircrafts: IAircraft[],
    timelineGL: TimelineGL,
    flights: IAircraftFlights,
    isFlight: boolean
  ): boolean => {
    const aircraft1: IAircraft = allAircrafts.find((aircraft: IAircraft) => aircraft._id === aircraftIds?.[0]);
    const aircraft2: IAircraft = allAircrafts.find((aircraft: IAircraft) => aircraft._id === aircraftIds?.[1]);

    const aircraft1YPos = timelineGL.getYPixelFromAircraft(aircraft1._id, allAircrafts);
    const aircraft2YPos = timelineGL.getYPixelFromAircraft(aircraft2._id, allAircrafts);

    const aircraft1Events = flights[aircraft2._id];
    const aircraft2Events = flights[aircraft1._id];

    const aircraftGroundEvent1 = findEventAtPosition(aircraft1Events.groundEvents, aircraft2YPos, timelineGL);
    const aircraftGroundEvent2 = findEventAtPosition(aircraft2Events.groundEvents, aircraft1YPos, timelineGL);

    if (!aircraft1 || !aircraft2) {
      return null;
    }

    let aircraft1Conflict = false;
    let aircraft2Conflict = false;

    if (aircraftGroundEvent1) {
      const groundEvent = aircraftGroundEvent1 as IGroundEvent;
      const selectedFlightEvents = selectedFlights;
      aircraft1Conflict = isFlight
        ? checkForConflict({
            groundEvents: groundEvent,
            reassignedEvents: Array(selectedFlightEvents[0]) as IFlight[],
          })
        : checkForConflict({
            groundEvents: groundEvent,
            reassignedGroundEvents: Array(selectedFlightEvents[0]) as IGroundEvent[],
          });
    } else if (aircraftGroundEvent2) {
      const groundEvent = aircraftGroundEvent2 as IGroundEvent;
      const selectedFlightEvents = selectedFlights;
      aircraft2Conflict = isFlight
        ? checkForConflict({
            groundEvents: groundEvent,
            reassignedEvents: Array(selectedFlightEvents[1]) as IFlight[],
          })
        : checkForConflict({
            groundEvents: groundEvent,
            reassignedGroundEvents: Array(selectedFlightEvents[1]) as IGroundEvent[],
          });
    }

    if (aircraft1Conflict || aircraft2Conflict) return true;
    else return false;
  };

  const swapAircraftsEvents = async (isFlight: boolean): Promise<void> => {
    handleCloseContext();
    const conflict = checkConflictBeforeSwap(
      isFlight ? selectedFlights : selectedGroundEvents,
      isFlight ? selectedEventsAircraft : selectedGroundEventsAircraft,
      allAircrafts,
      timelineGL,
      flights,
      isFlight
    );

    if (conflict) {
      const userResponse = await displayWarningModal();
      if (!userResponse) return;
    }

    dispatch(
      openSnackbar({
        open: true,
        closeDelay: 3000,
        variant: "info",
        message: "Swapping aircrafts...",
      })
    );

    const response = await swapAircraftBySelectedEvent(
      isFlight ? selectedFlights : selectedGroundEvents,
      isFlight ? selectedEventsAircraft : selectedGroundEventsAircraft,
      allAircrafts,
      isFlight
    );
    setBatchId1(response.batchId1);
    setBatchId2(response.batchId2);
  };

  const releaseFlight = (): void => {
    setActiveEventModal(EventModalTypes.ReleaseFlight);
    handleCloseContext();
  };

  const bulkEditEvent = (): void => {
    setActiveEventModal(EventModalTypes.BulkEditEvents);
    handleCloseContext();
  };

  const configFlightsAircraft = (): void => {
    handleCloseContext();
    setActiveEventModal(EventModalTypes.ConfigAircraft);
  };

  const groundTurnBack = (): void => {
    handleCloseContext();
    setActiveEventModal(EventModalTypes.GroundTurnBack);
  };

  const delayEvent = (): void => {
    setActiveEventModal(EventModalTypes.DelayEvent);
    handleCloseContext();
  };

  const setNewStartTime = (): void => {
    setActiveEventModal(EventModalTypes.SetNewStartTime);
    handleCloseContext();
  };

  const cancelEvent = (): void => {
    setActiveEventModal(EventModalTypes.CancelEvent);
    handleCloseContext();
  };

  const deleteEvents = (): void => {
    setActiveEventModal(EventModalTypes.DeleteEvent);
    handleCloseContext();
  };

  const unassignFlights = (): void => {
    setActiveEventModal(EventModalTypes.UnassignFlight);
    handleCloseContext();
  };

  const getConnections = (): void => {
    if (selectedFlights.length > 0) {
      dispatch(
        getFlightConnections(
          id,
          selectedFlights.map((f) => f._id)
        )
      );
      handleCloseContext();
    }
  };

  const reassignEvents = (): void => {
    if (selectedFlights.length > 0) setActiveEventModal(EventModalTypes.ReassignFlight);
    else if (selectedGroundEvents.length > 0) setActiveEventModal(EventModalTypes.ReassignGroundEvent);
  };

  const selectedFlightIds = useMemo(() => {
    return selectedFlights.map((flight) => flight._id);
  }, [selectedFlights]);

  const openFlightHistoryWindow = (): void => {
    const { path } = props;
    if (selectedFlightIds.length !== 1) {
      return;
    }
    dispatch(
      openHistoryWindow(
        "Flight History",
        {
          collection: "flights",
          id: selectedFlightIds[0],
          flight: selectedFlights[0],
        },
        path
      )
    );
    handleCloseContext();
  };

  const openGroundEventHistoryWindow = (): void => {
    const { path } = props;
    if (selectedGroundEvents.length !== 1) {
      return;
    }
    dispatch(
      openHistoryWindow(
        "Ground Event History",
        {
          collection: "groundevents",
          id: selectedGroundEvents[0]._id,
          groundEvent: selectedGroundEvents[0],
        },
        path
      )
    );
    handleCloseContext();
  };

  const createNewPairing = (): void => {
    setActiveEventModal(EventModalTypes.CreatePairing);
    handleCloseContext();
  };

  const assignFlightsToPairing = (): void => {
    setActiveEventModal(EventModalTypes.AssignToPairing);
    handleCloseContext();
  };

  const generateAPISAction = (): void => {
    if (selectedFlightIds.length !== 1) {
      return;
    }
    setActiveEventModal(EventModalTypes.SendAPISMessage);
  };

  const renderContextMenu = (theme: any): JSX.Element => {
    switch (true) {
      case !!selectedDay && !!selectedAircraft:
        return (
          <Card>
            <CardContent id={`${id}-unassignedflights`} sx={chipContainerStyles(theme)}>
              <Grid container direction={"row"} justifyContent={"center"}>
                <DayContextMenu selectedDay={selectedDay} selectedAircraftId={selectedAircraftId} />
              </Grid>
            </CardContent>
          </Card>
        );
      case selectedFlights.length === 1: {
        const displayFlightErrors = (): IFlightError[] => {
          const flightId = selectedFlights[0]._id;
          const aircraftId = selectedFlights[0].aircraft?._id ?? unassignedAircraftId;
          const flightErrors = flightErrorsMap[aircraftId]?.errors.filter(
            (value: IFlightError) => value.flightId === flightId
          );
          return flightErrors ?? [];
        };
        return (
          <Card sx={{ width: "100%" }}>
            <CardContent id={`${id}-flightevents`} sx={chipContainerStyles(theme)}>
              <Grid container direction={"row"} justifyContent={"center"}>
                <FlightChipCollection flights={selectedFlights} />
                <Grid container spacing={0} sx={{ my: 1 }}>
                  <Grid item xs={12}>
                    {displayFlightErrors().map((error: IFlightError, index: number) => {
                      return (
                        <Grid
                          container
                          rowSpacing={1}
                          key={index}
                          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                        >
                          <Grid
                            item
                            xs
                            sx={{ fontSize: "0.9em", minHeight: "30px", alignItems: "center", display: "flex" }}
                          >
                            <Tooltip placement="left" title={error.description}>
                              <Box>{error.title}</Box>
                            </Tooltip>
                          </Grid>
                          <Grid item xs={"auto"}>
                            <Chip
                              label={error.priority}
                              color={error.priority === "CRITICAL" ? "error" : "warning"}
                              size={"small"}
                              sx={(theme) => ({
                                fontSize: "0.7em",
                                width: "70px",
                                borderRadius: "4px",
                                height: "20px",
                                fontWeight: 900,
                                m: "1px",
                                ".MuiChip-label": { px: "5px", py: "1px", m: "1px", color: theme.palette.text.primary },
                              })}
                            />
                          </Grid>
                        </Grid>
                      );
                    })}
                  </Grid>
                  <Grid item xs={12} sx={{ mt: 1.5, display: "flex", justifyContent: "center" }}>
                    {selectedFlights[0].flags &&
                      selectedFlights[0].flags.length > 0 &&
                      selectedFlights[0].flags.map((flag: IFlightFlagSubDoc, key: number) => {
                        const fullFlightFlag = flightFlags.find((structureFlag: IFlightFlag) => {
                          return flag._id === structureFlag._id;
                        });
                        if (fullFlightFlag) {
                          return (
                            <Tooltip title={flag.title} key={key}>
                              <Box
                                sx={{ mr: "3px", display: "inline-flex", "& svg": { width: "24px", height: "24px" } }}
                              >
                                <div dangerouslySetInnerHTML={{ __html: fullFlightFlag.icon }} />
                              </Box>
                            </Tooltip>
                          );
                        } else {
                          return <Fragment key={key}></Fragment>;
                        }
                      })}
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
            <FlightSummaryList flightIds={selectedFlightIds} disabled={!isFlightContextOpen} />
            <DocumentAcknowledgements flightId={selectedFlightIds[0]} id={id} />
            <CardContent sx={{ p: 0 }}>
              {eventEngineIsAllowed && customFlightActions?.length > 0 && (
                <Grid container spacing={0}>
                  <Grid item xs={12} sx={{ p: 0, m: 0 }}>
                    <Accordion defaultExpanded>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={"flight-summary-custom-actionsContent"}
                        id={"flight-summary-custom-actions"}
                      >
                        Custom Actions
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0, "& .MuiMenuItem-root": { fontSize: "0.9em !important" } }}>
                        {customFlightActions.map((action, index) => (
                          <Tooltip
                            placement="left"
                            key={`custom_flight_action_${index}`}
                            title={action?.actionSettings?.userDescription}
                          >
                            <MenuItem onClick={() => handleCustomAction(action, selectedFlights)}>
                              {action.actionSettings.text}
                            </MenuItem>
                          </Tooltip>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        );
      }
      case selectedFlights.length > 1:
        return (
          <Card>
            <CardContent id={`${id}-flightevents`} sx={chipContainerStyles(theme)}>
              <FlightChipCollection flights={selectedFlights} />
            </CardContent>
            <CardContent sx={{ p: 0, "& .MuiMenuItem-root": { fontSize: "0.9em !important" } }}>
              <MenuList>
                {eventEngineIsAllowed && customFlightActions?.length > 0 && (
                  <Grid container>
                    <Grid item xs={12}>
                      <Accordion defaultExpanded>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls={"flight-summary-custom-actionsContent"}
                          id={"flight-summary-custom-actions"}
                        >
                          Custom Actions
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                          {customFlightActions.map((action, index) => (
                            <Tooltip
                              placement="left"
                              key={`custom_flight_action_${index}`}
                              title={action?.actionSettings?.userDescription}
                            >
                              <MenuItem onClick={() => handleCustomAction(action, selectedFlights)}>
                                {action.actionSettings.text}
                              </MenuItem>
                            </Tooltip>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </Grid>
                )}
                <Divider sx={{ mb: 2 }} />
                <MenuItem
                  sx={{ fontSize: "1.1em !important", fontWeight: 900, mt: 1 }}
                  onClick={bulkEditEvent}
                  disabled={selectedFlights?.length < 2 || !flightEditorIsAllowed}
                >
                  Bulk Edit Tool
                </MenuItem>
                <Divider />
                {selectedEventsOnTwoAircraft && (
                  <SwapACBatchTracker
                    batchId1={batchId1}
                    batchId2={batchId2}
                    numberOfActions={selectedEvents.length}
                    setBatchId1={setBatchId1}
                    setBatchId2={setBatchId2}
                  />
                )}
                <MenuItem onClick={() => swapAircraftsEvents(true)} disabled={!selectedEventsOnTwoAircraft}>
                  Swap Aircraft Flights
                </MenuItem>
                <MenuItem disabled={onlyUnassignedSelected} onClick={unassignFlights}>
                  Un-Assign Flights
                </MenuItem>
                <MenuItem onClick={reassignEvents}>Re-Assign Flights</MenuItem>
                <MenuItem onClick={delayEvent}>Delay/Expedite flights</MenuItem>
                <MenuItem onClick={setNewStartTime}>Set New Start Time</MenuItem>
                <MenuItem onClick={configFlightsAircraft}>Change Aircraft Config</MenuItem>
                <MenuItem onClick={getConnections}>Flight Connections</MenuItem>
                <Divider />
                <MenuItem disabled={selectedUnassigned?.length > 0} onClick={createNewPairing}>
                  Create Pairing from Selected Flights
                </MenuItem>
                <MenuItem disabled={selectedUnassigned?.length > 0} onClick={assignFlightsToPairing}>
                  Add Flights to Existing Pairing
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    const flightText = textualFlightInfoFormat(selectedEvents, flightTimelineCopyTextFormat);
                    navigator.clipboard.writeText(flightText);
                  }}
                >
                  Copy Flight Descriptions
                </MenuItem>
                <Divider />
                <MenuItem onClick={deleteEvents}>Delete Flights</MenuItem>
                <MenuItem onClick={cancelEvent}>Cancel Flights</MenuItem>
              </MenuList>
            </CardContent>
          </Card>
        );
      case selectedGroundEvents.length === 1:
        return (
          <Card sx={{ width: "100%" }}>
            <CardContent id={`${id}-groundevents`} sx={chipContainerStyles(theme)}>
              <EventChipCollection events={selectedGroundEvents} />
            </CardContent>
            <CardContent sx={{ p: 0, "& .MuiMenuItem-root": { fontSize: "0.9em !important" } }}>
              <MenuList>
                {eventEngineIsAllowed && customGroundEventActions?.length > 0 && (
                  <Grid container>
                    <Grid item xs={12}>
                      <Accordion defaultExpanded>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls={"flight-summary-custom-actionsContent"}
                          id={"flight-summary-custom-actions"}
                        >
                          Custom Actions
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                          {customGroundEventActions.map((action, index) => (
                            <Tooltip
                              placement="left"
                              key={`custom_groundevent_action_${index}`}
                              title={action?.actionSettings?.userDescription}
                            >
                              <MenuItem onClick={() => handleCustomAction(action, selectedGroundEvents)}>
                                {action?.actionSettings?.text}
                              </MenuItem>
                            </Tooltip>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </Grid>
                )}
                <Divider sx={{ mb: 2 }} />
                <MenuItem
                  sx={{ fontSize: "1.1em !important", fontWeight: 900, mt: 1 }}
                  onClick={() => handleBetaEditEvent(false)}
                  disabled={!groundEventEditorIsAllowed}
                >
                  Ground Event Editor
                </MenuItem>
                <Divider />
                <MenuItem onClick={reassignEvents}>Re-Assign Ground Event</MenuItem>
                <MenuItem onClick={delayEvent} disabled={!groundEventEditorIsAllowed}>
                  Delay/Expedite Ground Event
                </MenuItem>
                <MenuItem onClick={setNewStartTime} disabled={!groundEventEditorIsAllowed}>
                  {selectedGroundEvents.length === 1 ? "Change Times" : "Set New Start Time"}
                </MenuItem>
                <Divider />
                <MenuItem onClick={openGroundEventHistoryWindow} disabled={!groundEventEditorIsAllowed}>
                  View Ground Event History
                </MenuItem>
                <Divider />
                <MenuItem onClick={deleteEvents} disabled={!groundEventEditorIsAllowed}>
                  Delete Ground Event
                </MenuItem>
                <MenuItem onClick={cancelEvent} disabled={!groundEventEditorIsAllowed}>
                  Cancel Ground Event
                </MenuItem>
              </MenuList>
            </CardContent>
          </Card>
        );
      case selectedGroundEvents.length > 1:
        return (
          <Card sx={{ width: "100%" }}>
            <CardContent id={`${id}-groundevents`} sx={chipContainerStyles(theme)}>
              <EventChipCollection events={selectedGroundEvents} />
            </CardContent>
            <CardContent sx={{ p: 0, "& .MuiMenuItem-root": { fontSize: "0.9em !important" } }}>
              <MenuList>
                {eventEngineIsAllowed && customGroundEventActions?.length > 0 && (
                  <Grid container>
                    <Grid item xs={12}>
                      <Accordion defaultExpanded>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls={"flight-summary-custom-actionsContent"}
                          id={"flight-summary-custom-actions"}
                        >
                          Custom Actions
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                          {customGroundEventActions.map((action, index) => (
                            <Tooltip
                              placement="left"
                              key={`custom_groundevent_action_${index}`}
                              title={action?.actionSettings?.userDescription}
                            >
                              <MenuItem onClick={() => handleCustomAction(action, selectedGroundEvents)}>
                                {action?.actionSettings?.text}
                              </MenuItem>
                            </Tooltip>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </Grid>
                )}{" "}
                <Divider sx={{ mb: 2 }} />
                <MenuItem
                  sx={{ fontSize: "1.1em !important", fontWeight: 900, mt: 1 }}
                  onClick={bulkEditEvent}
                  disabled={!groundEventEditorIsAllowed}
                >
                  Bulk Edit Tool
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => swapAircraftsEvents(false)} disabled={!selectedGroundEventsOnTwoAircraft}>
                  Swap Aircraft Ground Events
                </MenuItem>
                <MenuItem onClick={reassignEvents} disabled={!groundEventEditorIsAllowed}>
                  Re-Assign Ground Events
                </MenuItem>
                <MenuItem onClick={delayEvent} disabled={!groundEventEditorIsAllowed}>
                  Delay/Expedite Ground Events
                </MenuItem>
                <MenuItem onClick={setNewStartTime} disabled={!groundEventEditorIsAllowed}>
                  Set New Start Times
                </MenuItem>
                <Divider />
                <MenuItem onClick={deleteEvents} disabled={!groundEventEditorIsAllowed}>
                  Delete Ground Events
                </MenuItem>
                <MenuItem onClick={cancelEvent} disabled={!groundEventEditorIsAllowed}>
                  Cancel Ground Events
                </MenuItem>
              </MenuList>
            </CardContent>
          </Card>
        );
      default:
        return null;
    }
  };

  const menuTitle = useMemo((): string => {
    if (!!selectedDay && !!selectedAircraft) {
      return "Selected Day/Aircraft Options";
    }
    if (selectedFlights.length === 1) {
      return "Selected Flight Options";
    }
    if (selectedFlights.length > 1) {
      return "Selected Flights Options";
    }
    if (selectedGroundEvents.length === 1) {
      return "Selected Ground Event Options";
    }
    if (selectedGroundEvents.length > 1) {
      return "Selected Ground Events Options";
    }
    return "";
  }, [selectedFlights, selectedGroundEvents, selectedAircraft, selectedDay]);

  const computedWidth = selectedFlights.length === 1 ? "clamp(300px, 32vw, 470px)" : "clamp(280px, 28vw, 300px)";

  return (
    <ContextMenu
      id={`${id}-contextMenu`}
      isOpen={isFlightContextOpen}
      key={`${id}-contextmenu`}
      maxWidth={computedWidth}
      onClose={handleCloseContext}
      menuHeader={<Box>{menuTitle}</Box>}
    >
      <Grid
        container
        spacing={0}
        sx={{
          width: "auto",
        }}
      >
        {selectedFlights.length === 1 && (
          <Grid
            item
            xs={"auto"}
            sx={{
              backgroundColor: theme.palette.background.default,
              maxWidth: "150px !important",
              display: "flex",
              alignItems: centerMenu ? "center" : "start",
              justifyContent: "center",
              "& .MuiMenuItem-root": { fontSize: "0.9em !important", pl: "6px" },
            }}
          >
            {" "}
            <MenuList>
              <MenuItem onClick={() => swapAircraftsEvents(true)} disabled={!selectedEventsOnTwoAircraft}>
                Swap Aircraft Flights
              </MenuItem>
              <MenuItem disabled={onlyUnassignedSelected} onClick={unassignFlights}>
                Un-Assign Flight
              </MenuItem>
              <MenuItem onClick={reassignEvents}>Re-Assign Flight</MenuItem>
              <MenuItem onClick={delayEvent}>Delay/Expedite Flight</MenuItem>
              <MenuItem onClick={setNewStartTime}>Set New Start Time</MenuItem>
              <MenuItem onClick={groundTurnBack}>Ground Turn Back</MenuItem>
              <MenuItem onClick={getConnections}>Flight Connections</MenuItem>
              <Divider />
              <MenuItem onClick={selectByFlightNumber}>Select By Flight #</MenuItem>
              <MenuItem onClick={selectByFlightAndReturnFlightNumber}>Select By # &amp; Return</MenuItem>
              <MenuItem onClick={selectFlightsUntilReturn}>Select Return Flight</MenuItem>
              <Divider />
              <MenuItem
                sx={{ fontWeight: 900 }}
                onClick={() => handleBetaEditEvent(true)}
                disabled={selectedFlights.length > 1 || !flightEditorIsAllowed}
              >
                Flight Editor
              </MenuItem>
              <MenuItem onClick={() => releaseFlight()} disabled={selectedFlights.length > 1 || !flightEditorIsAllowed}>
                {selectedFlights[0].isReleased ? (
                  "Re-Release Flight"
                ) : (
                  <>
                    <div className="unreleased_icon" />
                    Release Flight
                  </>
                )}
              </MenuItem>
              <MenuItem onClick={handleSendMovementMessage}>Submit Movement Msg.</MenuItem>
              {isAPISCustomer && <MenuItem onClick={generateAPISAction}>Generate APIS</MenuItem>}
              <Divider />
              <MenuItem disabled={selectedUnassigned?.length > 0} onClick={createNewPairing}>
                New Pairing from Flight
              </MenuItem>
              <MenuItem disabled={selectedUnassigned?.length > 0} onClick={assignFlightsToPairing}>
                Add to Existing Pairing
              </MenuItem>
              <Divider />
              <MenuItem onClick={openFlightHistoryWindow}>View Flight History</MenuItem>
              <MenuItem
                onClick={() => {
                  const flightText = textualFlightInfoFormat(selectedEvents, flightTimelineCopyTextFormat);
                  navigator.clipboard.writeText(flightText);
                }}
              >
                Copy Flight Desc.
              </MenuItem>
              <Divider />
              <MenuItem onClick={deleteEvents}>Delete Flight</MenuItem>
              <MenuItem onClick={cancelEvent}>Cancel Flight</MenuItem>
            </MenuList>
          </Grid>
        )}
        <Grid
          item
          xs
          sx={(them) => ({
            display: "flex",
            alignItems: "flex-start",
            borderLeft: selectedFlights.length === 1 ? `2px solid ${theme.palette.grey[500]}` : "none",
          })}
        >
          {renderContextMenu(theme)}
        </Grid>
      </Grid>
    </ContextMenu>
  );
};

export const selectByFlightNumberPredicate = (flight: IFlight[], selectedIndex: number): IFlight[] => {
  const number = flight[selectedIndex].flightNumber;
  const selectedFlights = new Array<IFlight>();
  for (let i = selectedIndex; i < flight.length; i++) {
    if (flight[i].flightNumber === number) {
      selectedFlights.push(flight[i]);
    } else break;
  }
  return selectedFlights;
};

export const selectByPredicate = (
  selectedFlight: IFlight,
  flights: IAircraftFlights,
  predicate: (flights: IFlight[], selectedIndex: number) => IFlight[]
): IFlight[] => {
  const aircraft = selectedFlight.aircraft ? selectedFlight.aircraft._id : unassignedAircraftId;
  const aircraftFlights = flights[aircraft].flights;
  const startIndex = aircraftFlights.findIndex((value: IFlight) => value._id === selectedFlight._id);
  return predicate([...aircraftFlights], startIndex);
};
